import { useEffect, useState, useMemo, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useTransactions, Transaction } from '@/hooks/useTransactions';
import { useCustomersSaas } from '@/hooks/useCustomersSaas';
import { useProductPlans } from '@/hooks/useProductPlans';
import { useProducts } from '@/hooks/useProducts';
import { useRenewalDetection } from '@/hooks/useRenewalDetection';
import { CurrencyInput } from '@/components/CurrencyInput';
import { RenewalIndicator } from './RenewalIndicator';
import { ManualRenewalOverride } from './ManualRenewalOverride';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const formSchema = z.object({
  customer_saas_id: z.string().min(1, 'Cliente é obrigatório'),
  product_id: z.string().min(1, 'Produto é obrigatório'),
  product_plan_id: z.string().min(1, 'Plano é obrigatório'),
  payment_date: z.date({ required_error: 'Data é obrigatória' }),
  amount_paid: z.number().min(0.01, 'Valor deve ser maior que zero'),
  currency: z.string().default('BRL'),
  payment_status: z.enum(['paid', 'pending', 'refunded', 'failed']),
  data_source: z.enum(['api', 'spreadsheet', 'manual']),
  document: z.string().optional(),
  gateway_transaction_id: z.string().optional(),
  description: z.string().optional(),
  notes: z.string().optional(),
  force_as_renewal: z.boolean().default(false),
  manual_previous_transaction_id: z.string().optional(),
}).refine(
  (data) => {
    if (data.force_as_renewal && !data.manual_previous_transaction_id) {
      return false;
    }
    return true;
  },
  {
    message: 'Selecione a transação anterior ao forçar renovação',
    path: ['manual_previous_transaction_id'],
  }
);

type FormValues = z.infer<typeof formSchema>;

interface TransactionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transaction?: Transaction | null;
}

export function TransactionDialog({ open, onOpenChange, transaction }: TransactionDialogProps) {
  const { createTransaction, updateTransaction, transactions } = useTransactions();
  const { customers, findOrCreateCustomerSaas } = useCustomersSaas();
  const { products } = useProducts();
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [showManualOverride, setShowManualOverride] = useState(false);
  const [isLoadingTransaction, setIsLoadingTransaction] = useState(false);
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>('');
  const [isCreatingCustomerSaas, setIsCreatingCustomerSaas] = useState(false);
  
  // Buscar todos os planos para encontrar o produto na edição
  const { plans: allPlans } = useProductPlans();
  // Buscar planos filtrados para o produto selecionado
  const { plans: filteredPlans } = useProductPlans(selectedProductId);
  
  // Usar planos filtrados se houver produto selecionado, senão usar todos
  const productPlans = selectedProductId ? filteredPlans : allPlans;

  // Agrupar clientes únicos por nome
  const uniqueCustomers = useMemo(() => {
    if (!customers) return [];
    
    const grouped = new Map<string, typeof customers[0]>();
    
    customers.forEach(customer => {
      if (!grouped.has(customer.name)) {
        grouped.set(customer.name, customer);
      }
    });
    
    return Array.from(grouped.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  }, [customers]);

  // Função para buscar ou criar customer_saas_id correto
  const resolveCustomerSaasId = useCallback(async (
    customerName: string, 
    productId: string
  ): Promise<string | null> => {
    if (!customers || !customerName || !productId) return null;
    
    // Buscar customer_saas existente para esse cliente + produto
    const match = customers.find(
      c => c.name === customerName && c.product_id === productId
    );
    
    if (match) {
      return match.id; // Já existe
    }
    
    // Se não existe, precisa criar
    setIsCreatingCustomerSaas(true);
    
    try {
      const newCustomerSaasId = await findOrCreateCustomerSaas.mutateAsync({
        customerName,
        productId,
      });
      
      return newCustomerSaasId;
    } catch (error) {
      console.error('Erro ao criar customer_saas:', error);
      toast.error('Erro ao vincular cliente ao produto');
      return null;
    } finally {
      setIsCreatingCustomerSaas(false);
    }
  }, [customers, findOrCreateCustomerSaas]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      customer_saas_id: '',
      product_id: '',
      product_plan_id: '',
      amount_paid: 0,
      currency: 'BRL',
      payment_status: 'paid',
      data_source: 'manual',
      document: '',
      gateway_transaction_id: '',
      description: '',
      notes: '',
      force_as_renewal: false,
      manual_previous_transaction_id: '',
    },
  });

  // Hook de detecção de renovação
  const customerId = form.watch('customer_saas_id');
  const planId = form.watch('product_plan_id');

  const { data: renewalInfo, isLoading: isCheckingRenewal } = useRenewalDetection(
    customerId,
    planId,
    !!customerId && !!planId,
    transaction?.id
  );

  // Limpar plano e valor quando produto mudar (mas não ao carregar transação)
  useEffect(() => {
    // Não limpar campos se estamos carregando uma transação existente OU se já existe transação
    if (isLoadingTransaction || transaction) return;
    
    form.setValue('product_plan_id', '');
    form.setValue('amount_paid', 0);
  }, [selectedProductId, isLoadingTransaction, transaction]);

  // Auto-preencher valor quando plano mudar
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'product_plan_id' && value.product_plan_id) {
        const selectedPlan = productPlans?.find(p => p.id === value.product_plan_id);
        if (selectedPlan) {
          form.setValue('amount_paid', selectedPlan.price);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [form, productPlans]);

  useEffect(() => {
    if (transaction) {
      setIsLoadingTransaction(true);
      
      // Buscar product_id do plano selecionado em todos os planos
      const plan = allPlans?.find(p => p.id === transaction.product_plan_id);
      const productId = plan?.product_id || '';
      
      // Buscar nome do cliente
      const customerRecord = customers?.find(c => c.id === transaction.customer_saas_id);
      if (customerRecord) {
        setSelectedCustomerName(customerRecord.name);
      }
      
      setSelectedProductId(productId);
      
      form.reset({
        customer_saas_id: transaction.customer_saas_id,
        product_id: productId,
        product_plan_id: transaction.product_plan_id || '',
        payment_date: new Date(transaction.payment_date),
        amount_paid: transaction.amount_paid,
        currency: transaction.currency,
        payment_status: transaction.payment_status,
        data_source: transaction.data_source,
        document: transaction.document || '',
        gateway_transaction_id: transaction.gateway_transaction_id || '',
        description: transaction.description || '',
        notes: transaction.notes || '',
      });
      
      // Resetar flag após formulário ser populado
      setTimeout(() => setIsLoadingTransaction(false), 100);
    } else {
      setIsLoadingTransaction(false);
      setSelectedProductId('');
      setSelectedCustomerName('');
      form.reset({
        customer_saas_id: '',
        product_id: '',
        product_plan_id: '',
        amount_paid: 0,
        currency: 'BRL',
        payment_status: 'paid',
        data_source: 'manual',
        document: '',
        gateway_transaction_id: '',
        description: '',
        notes: '',
      });
    }
  }, [transaction, form, allPlans, customers]);

  const onSubmit = async (values: FormValues) => {
    const payload: any = {
      customer_saas_id: values.customer_saas_id,
      product_plan_id: values.product_plan_id,
      payment_date: format(values.payment_date, 'yyyy-MM-dd'),
      amount_paid: values.amount_paid,
      currency: values.currency,
      payment_status: values.payment_status,
      data_source: values.data_source,
      document: values.document || null,
      gateway_transaction_id: values.gateway_transaction_id || null,
    };

    // Override manual: Se forçado como renovação manualmente
    if (values.force_as_renewal && values.manual_previous_transaction_id) {
      payload.is_renewal = true;
      payload.previous_transaction_id = values.manual_previous_transaction_id;
    }

    // Incluir description/notes apenas na edição
    if (transaction) {
      payload.description = values.description || null;
      payload.notes = values.notes || null;
    }

    if (transaction) {
      await updateTransaction.mutateAsync({ id: transaction.id, updates: payload });
    } else {
      await createTransaction.mutateAsync(payload);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{transaction ? 'Editar Transação' : 'Nova Transação'}</DialogTitle>
          <DialogDescription>
            Preencha os dados da transação
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Cliente - agrupado por nome */}
            <FormField
              control={form.control}
              name="customer_saas_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cliente *</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      setSelectedCustomerName(value);
                      // Limpar seleções dependentes
                      field.onChange('');
                      form.setValue('product_id', '');
                      form.setValue('product_plan_id', '');
                      setSelectedProductId('');
                    }} 
                    value={selectedCustomerName}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um cliente" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {uniqueCustomers?.map((customer) => (
                        <SelectItem key={customer.name} value={customer.name}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
              {/* Produto - com auto-criação de customer_saas */}
              <FormField
                control={form.control}
                name="product_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Produto *</FormLabel>
                    <Select 
                      onValueChange={async (value) => {
                        field.onChange(value);
                        setSelectedProductId(value);
                        
                        // Buscar ou criar customer_saas_id automaticamente
                        if (selectedCustomerName && value) {
                          const customerSaasId = await resolveCustomerSaasId(
                            selectedCustomerName, 
                            value
                          );
                          
                          if (customerSaasId) {
                            form.setValue('customer_saas_id', customerSaasId);
                          }
                        }
                      }} 
                      value={field.value}
                      disabled={!selectedCustomerName || isCreatingCustomerSaas}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue 
                            placeholder={
                              !selectedCustomerName 
                                ? "Selecione um cliente primeiro" 
                                : isCreatingCustomerSaas
                                ? "Criando vínculo..."
                                : "Selecione um produto"
                            } 
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {products?.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!selectedCustomerName && (
                      <FormDescription>
                        Selecione um cliente primeiro
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Plano do Produto - agora filtrado */}
              <FormField
                control={form.control}
                name="product_plan_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Plano do Produto *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione um plano" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {productPlans?.map((plan) => (
                          <SelectItem key={plan.id} value={plan.id}>
                            {plan.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
              </FormItem>
                )}
              />
            </div>

            {/* OPÇÃO 2: Indicador Visual */}
            {customerId && planId && (
              <RenewalIndicator
                isRenewal={renewalInfo?.isRenewal || false}
                renewalCount={renewalInfo?.renewalCount || 0}
                lastTransactionDate={renewalInfo?.lastTransactionDate || null}
                isLoading={isCheckingRenewal}
              />
            )}

            {/* Botão para mostrar override manual */}
            {customerId && !transaction && (
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowManualOverride(!showManualOverride)}
                >
                  {showManualOverride ? 'Ocultar' : 'Mostrar'} opções avançadas
                </Button>
              </div>
            )}

            {/* OPÇÃO 3: Override Manual */}
            {showManualOverride && customerId && (
              <ManualRenewalOverride
                form={form}
                customerId={customerId}
                availableTransactions={transactions || []}
                isDetectedAsRenewal={renewalInfo?.isRenewal || false}
              />
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Data do Pagamento */}
              <FormField
                control={form.control}
                name="payment_date"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Data do Pagamento *</FormLabel>
                    <Popover>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              'w-full pl-3 text-left font-normal',
                              !field.value && 'text-muted-foreground'
                            )}
                          >
                            {field.value ? (
                              format(field.value, 'PPP', { locale: ptBR })
                            ) : (
                              <span>Selecione uma data</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={field.onChange}
                          locale={ptBR}
                          className="pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Valor Pago - CurrencyInput com auto-preenchimento */}
              <FormField
                control={form.control}
                name="amount_paid"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Valor Pago *</FormLabel>
                    <FormControl>
                      <CurrencyInput
                        value={field.value}
                        currency={form.watch('currency') as 'BRL' | 'USD'}
                        onValueChange={field.onChange}
                        onCurrencyChange={(curr) => form.setValue('currency', curr)}
                        showLabel={false}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Documento - NOVO campo opcional */}
              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Documento (CPF/CNPJ)</FormLabel>
                    <FormControl>
                      <Input placeholder="000.000.000-00" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Status */}
              <FormField
                control={form.control}
                name="payment_status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Status *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="paid">Pago</SelectItem>
                        <SelectItem value="pending">Pendente</SelectItem>
                        <SelectItem value="refunded">Estornado</SelectItem>
                        <SelectItem value="failed">Falhou</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Origem dos Dados */}
              <FormField
                control={form.control}
                name="data_source"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Origem dos Dados *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="manual">Manual</SelectItem>
                        <SelectItem value="api">API</SelectItem>
                        <SelectItem value="spreadsheet">Planilha</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* ID Gateway */}
              <FormField
                control={form.control}
                name="gateway_transaction_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>ID Gateway</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Descrição e Observações - APENAS NA EDIÇÃO */}
            {transaction && (
              <>
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Observações</FormLabel>
                      <FormControl>
                        <Textarea {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </>
            )}

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit">Salvar</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
